import { InboxOutlined } from "@ant-design/icons";
import { TrashIcon } from "@heroicons/react/16/solid";
import { PlusIcon } from "@heroicons/react/20/solid";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { message } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { api } from "../../../../../api";
import { WhatsappTemplateButtonTypeEnum } from "../../../../../enums";
import { generateWhatsappLink } from "../../../../../functions/generateWhatsappLink";
import { CustomerContext } from "../../../../../providers";
import { handleMessageMultipleChanges } from "../../../../handleMessageMultipleChanges";
import { Button } from "../../../../tailwind/button";
import { Divider } from "../../../../tailwind/divider";
import { ErrorMessage, Field, Legend } from "../../../../tailwind/fieldset";
import { Heading } from "../../../../tailwind/heading";
import { Input, InputGroup } from "../../../../tailwind/input";
import { Link } from "../../../../tailwind/link";
import { Text } from "../../../../tailwind/text";
import { Textarea } from "../../../../tailwind/textarea";
import { SwitchOnOff } from "../../../../tailwind/switch";
import { Tooltip } from "../../../../Tooltip";
import { addReplyButtonFunction } from "../../../addReplyButtonFunction";
import { onDeleteButtonFunction } from "../../../onDeleteButtonFunction";
import { useParameters } from "../hooks";
import SelectInput from "../../../../tailwind/select-input";
import { BodyFormSection } from "./BodyFormSection";
import ButtonToggleGroup from "./ButtonToggleGroup";
import QrCodePreview from "./QrCodePreview";
import { handleUploadFile } from "../../../../../functions/handleUploadFile";
import { handleBeforeUploadFile } from "../../../../../functions/handleBeforeUploadFile";

const ManageMessage = ({
  scenario,
  nodes,
  setNodes,
  edges,
  setEdges,
  handleTypeChange,
  handleButtonChange,
  nodeId,
  nodeData,
  setModifications,
  bodyRef,
  errors,
  setIsConvertCompressDialogOpen,
  setFile,
  setIsPanelOpened,
  selectedNodeConvertCompress,
  setSelectedNodeConvertCompress,
}) => {
  const headerTypes = {
    image: "image",
    audio: "audio",
    video: "video",
    document: "document",
    none: null,
  };

  const nodeDataType = nodeData?.type;
  const { user, integrations } = useContext(CustomerContext);

  const selectedNode = useMemo(() => {
    return nodes?.find((node) => node.selected);
  }, [nodes]);

  const [headerType, setHeaderType] = useState(
    (nodeData.image && headerTypes.image) ||
      (nodeData.audio && headerTypes.audio) ||
      (nodeData.document && headerTypes.document) ||
      (nodeData.video && headerTypes.video) ||
      headerTypes.none,
  );
  const [buttonsMode, setButtonsMode] = useState(
    ((nodeData.urlButton?.text || nodeData.urlButton?.url) &&
      WhatsappTemplateButtonTypeEnum.URL) ||
      (nodeData.buttons?.length &&
        WhatsappTemplateButtonTypeEnum.QUICK_REPLY) ||
      null,
  );
  const [isQrCodeVisible, setIsQrCodeVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedCustomField, setSelectedCustomField] = useState("");
  const urlButtonLinkRef = useRef(null);
  const buttonsContainerRef = useRef(null);

  const removeButtons = async () => {
    if (nodeData?.buttons?.length) {
      for (let index = nodeData.buttons.length - 1; index >= 0; index--) {
        await onDeleteButtonFunction({
          buttonIndex: index,
          api,
          setEdges,
          setNodes,
          edges,
          nodes,
          nodeId,
        });
      }
    }
  };

  const updateHeaderType = useCallback(
    async (_headerType) => {
      const _defaultMedias = {
        video: null,
        audio: null,
        image: null,
        document: null,
      };

      setHeaderType(_headerType);
      switch (_headerType) {
        case headerTypes.none:
        case headerTypes.video:
        case headerTypes.document:
        case headerTypes.image: {
          handleMessageMultipleChanges(_defaultMedias, nodes, setNodes);
          break;
        }
        case headerTypes.audio: {
          // An audio message is alone, no buttons, nothing
          await removeButtons();
          handleMessageMultipleChanges(
            {
              body: null,
              bodyParameters: null,
              footer: null,
              redirectionUrl: null,
              urlButton: null,
              urlButtonLink: null,
              ..._defaultMedias,
            },
            nodes,
            setNodes,
          );
          break;
        }
        default:
          break;
      }
    },
    [headerType, nodeData.buttons],
  );

  const headerOptions = [
    { value: headerTypes.none, label: "None" },
    { value: headerTypes.image, label: "Image" },
    { value: headerTypes.video, label: "Video" },
    { value: headerTypes.audio, label: "Audio" },
    { value: headerTypes.document, label: "Document" },
  ];

  const buttonsModeOptions = [
    { value: null, label: "None" },
    { value: WhatsappTemplateButtonTypeEnum.QUICK_REPLY, label: "Quick reply" },
    { value: WhatsappTemplateButtonTypeEnum.URL, label: "URL" },
  ];

  const updateButtonsMode = useCallback(
    async (value) => {
      await removeButtons();

      // Check if this is a WhatsApp order chatbot and set default URL
      let defaultUrlButton = null;
      if (
        value === WhatsappTemplateButtonTypeEnum.URL &&
        scenario?.chatbotType === "whatsapp_order" &&
        user?.customer?.shop?.domain
      ) {
        defaultUrlButton = {
          text: "Terminer ma commande",
          url: `https://${user.customer.shop.domain}/{{1}}`,
        };
      }

      handleMessageMultipleChanges(
        {
          buttons: null,
          urlButton: defaultUrlButton,
          urlButtonLink: defaultUrlButton?.url || null,
          footer: value ? (nodeData.footer ?? null) : null,
        },
        nodes,
        setNodes,
      );
      setButtonsMode(value);

      if (value === WhatsappTemplateButtonTypeEnum.QUICK_REPLY) {
        setTimeout(() => {
          buttonsContainerRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    },
    [buttonsMode, setButtonsMode, scenario, user],
  );

  const handleURLButtonChanged = useCallback(
    (e) => {
      handleMessageMultipleChanges(
        {
          urlButton: {
            text: e.target.value,
            url: nodeData.urlButton?.url ?? "",
            trackingEnabled: nodeData.urlButton?.trackingEnabled ?? true,
          },
        },
        nodes,
        setNodes,
        selectedNode,
      );
    },
    [selectedNode, nodes, nodeData.urlButton?.trackingEnabled],
  );

  const handleTrackingToggleChanged = useCallback(
    (enabled) => {
      handleMessageMultipleChanges(
        {
          urlButton: {
            text: nodeData.urlButton?.text ?? "",
            url: nodeData.urlButton?.url ?? "",
            trackingEnabled: enabled,
          },
        },
        nodes,
        setNodes,
        selectedNode,
      );
    },
    [selectedNode, nodes, nodeData.urlButton?.text, nodeData.urlButton?.url],
  );

  const handleURLButtonLinkChanged = useCallback(
    (e) => {
      const inputValue = e.target.value;
      const customFieldNames = Object.keys(user?.customer?.customFields || {});
      let urlForStorage = inputValue;
      const regex = /\{\{([^}]+)\}\}/g;

      urlForStorage = inputValue.replace(regex, (match, fieldName) => {
        if (customFieldNames.includes(fieldName)) {
          return match;
        }
        return "{{url_variable}}";
      });

      handleMessageMultipleChanges(
        {
          urlButton: {
            text: nodeData.urlButton?.text ?? "",
            url: urlForStorage,
            trackingEnabled: nodeData.urlButton?.trackingEnabled ?? true,
          },
          urlButtonLink: inputValue,
        },
        nodes,
        setNodes,
      );
    },
    [
      selectedNode,
      nodes,
      nodeData.urlButton?.text,
      nodeData.urlButton?.trackingEnabled,
      user?.customer?.customFields,
    ],
  );

  const addCustomFieldToUrl = () => {
    if (!selectedCustomField) return;
    const textarea = urlButtonLinkRef.current;
    const cursorPosition = textarea.selectionStart ?? textarea.value.length;
    const textBefore = textarea.value.substring(0, cursorPosition);
    const textAfter = textarea.value.substring(cursorPosition);
    const updatedValue = `${textBefore}{{${selectedCustomField}}}${textAfter}`;
    textarea.value = updatedValue ?? "";
    const newCursorPosition =
      cursorPosition + `{{${selectedCustomField}}}`.length;
    textarea.setSelectionRange(newCursorPosition, newCursorPosition);

    const customFieldNames = Object.keys(user?.customer?.customFields || {});
    const regex = /\{\{([^}]+)\}\}/g;

    const urlForStorage = updatedValue.replace(regex, (match, fieldName) => {
      if (customFieldNames.includes(fieldName)) {
        return match;
      }
      return "{{url_variable}}";
    });

    handleMessageMultipleChanges(
      {
        urlButton: {
          text: nodeData.urlButton?.text ?? "",
          url: urlForStorage,
          trackingEnabled: nodeData.urlButton?.trackingEnabled ?? true,
        },
        urlButtonLink: updatedValue,
      },
      nodes,
      setNodes,
    );
    setSelectedCustomField("");
  };

  const addUrlVariable = (ref) => {
    const textarea = ref.current;

    const updatedValue = `${textarea.value}{{url_variable}}`;
    textarea.value = updatedValue ?? "";

    const customFieldNames = Object.keys(user?.customer?.customFields || {});
    const regex = /\{\{([^}]+)\}\}/g;

    const urlForStorage = updatedValue.replace(regex, (match, fieldName) => {
      if (customFieldNames.includes(fieldName)) {
        return match;
      }
      return "{{url_variable}}";
    });

    handleMessageMultipleChanges(
      {
        urlButton: {
          text: nodeData.urlButton?.text ?? "",
          url: urlForStorage,
          trackingEnabled: nodeData.urlButton?.trackingEnabled ?? true,
        },
        urlButtonLink: updatedValue,
      },
      nodes,
      setNodes,
    );
  };

  function handleBodyChange(body) {
    handleMessageMultipleChanges({ body }, nodes, setNodes);
    setModifications(true);
  }

  function handleFooterChange(footer) {
    handleMessageMultipleChanges({ footer }, nodes, setNodes);
  }

  const currentWaMeLink = useMemo(() => {
    if (!user?.customer?.phoneNumber) {
      return "";
    }
    return generateWhatsappLink(user.customer.phoneNumber, nodeData.text);
  }, [user?.customer?.phoneNumber, nodeData.text]);

  const handleShowQrCode = useCallback(() => {
    setIsQrCodeVisible(true);
  }, []);

  const addReplyButton = ({ isInteractiveList = false }) => {
    addReplyButtonFunction({
      isInteractiveList,
      setNodes,
      nodes,
      nodeId,
    });

    setTimeout(() => {
      buttonsContainerRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const title = !nodeDataType ? "Select message type" : "Message edition";

  const { paramOptions } = useParameters({
    integrations,
    scenario,
    user,
  });

  const customFieldOptions = useMemo(() => {
    if (!user?.customer?.customFields) return [];
    return Object.keys(user.customer.customFields).map((fieldKey) => ({
      label: fieldKey,
      value: fieldKey,
    }));
  }, [user?.customer?.customFields]);

  const invalidCustomFieldInUrl = useMemo(() => {
    const url = nodeData.urlButton?.url ?? "";
    if (!url) return null;
    const customFieldNames = Object.keys(user?.customer?.customFields || {});
    const regex = /\{\{([^}]+)\}\}/g;
    let match;
    while ((match = regex.exec(url)) !== null) {
      const fieldName = match[1];
      if (
        fieldName !== "1" &&
        fieldName !== "url_variable" &&
        !customFieldNames.includes(fieldName)
      ) {
        return fieldName;
      }
    }
    return null;
  }, [nodeData.urlButton?.url, user?.customer?.customFields]);

  const hasCustomFieldInUrl = useMemo(() => {
    const url = nodeData.urlButton?.url ?? "";
    if (!url) {
      return false;
    }
    const regex = /\{\{([^}]+)\}\}/g;
    let match;
    while ((match = regex.exec(url)) !== null) {
      const fieldName = match[1];
      if (fieldName !== "1" && fieldName !== "url_variable") {
        return true;
      }
    }
    return false;
  }, [nodeData.urlButton?.url]);

  const customFieldCountInUrl = useMemo(() => {
    const url = nodeData.urlButton?.url ?? "";
    if (!url || !customFieldOptions.length) return 0;
    const customFieldNames = customFieldOptions.map((opt) => opt.value);
    let count = 0;
    customFieldNames.forEach((fieldName) => {
      if (url.includes(`{{${fieldName}}}`)) count++;
    });
    return count;
  }, [nodeData.urlButton?.url, customFieldOptions]);

  const urlVariableNotAtEnd = useMemo(() => {
    const url = nodeData.urlButton?.url ?? "";
    if (url.includes("{{url_variable}}") && !url.endsWith("{{url_variable}}")) {
      return true;
    }
    if (url.includes("{{1}}") && !url.endsWith("{{1}}")) {
      return true;
    }
    return false;
  }, [nodeData.urlButton?.url]);

  return (
    <>
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <Heading className="text-lg">{title}</Heading>
          <Tooltip
            content="An answer or message is free. It can only be sent in the 24-hour window after a contact has clicked on a quick reply button or sent a message."
            size="4"
            placement="bottom"
          />
        </div>
        {nodeDataType &&
          title !== "Select message type" &&
          !nodeData?.isTemplate && (
            <Link
              className="text-gray text-sm hover:underline text-gray-400 hover:cursor-pointer"
              onClick={() => handleTypeChange("", nodes)}
            >
              Change message type
            </Link>
          )}
      </div>

      {(nodeDataType ?? "") === "" && (
        <div className="my-5 grid  grid-cols-3 gap-5">
          <Button
            color="secondaryButton"
            onClick={async () => {
              await removeButtons();
              handleTypeChange("media", nodes);
              handleMessageMultipleChanges(
                {
                  buttons: null,
                  urlButton: null,
                  urlButtonLink: null,
                },
                nodes,
                setNodes,
              );
            }}
          >
            Text & Media
          </Button>
          <Button
            color="secondaryButton"
            onClick={async () => {
              await removeButtons();
              handleTypeChange("list", nodes);
              handleMessageMultipleChanges(
                {
                  buttons: null,
                  urlButton: null,
                  urlButtonLink: null,
                },
                nodes,
                setNodes,
              );
            }}
          >
            Survey
          </Button>
        </div>
      )}

      {nodeDataType === "list" && (
        <div className="mb-5">
          <Field>
            <Legend>Header</Legend>
            <div className="flex flex-row">
              <InputGroup className="flex relative items-center">
                <Input
                  name="Header"
                  value={nodeData.header ?? ""}
                  onChange={(e) =>
                    handleMessageMultipleChanges(
                      { header: e.target.value },
                      nodes,
                      setNodes,
                    )
                  }
                  placeholder="Message header"
                  autoComplete="off"
                  maxLength={60}
                  showCharacterCount
                />
              </InputGroup>
            </div>
          </Field>

          <Field>
            <Legend>Body</Legend>
            <Textarea
              name="Message body"
              placeholder="Message body"
              value={nodeData.body ?? ""}
              onChange={(e) => {
                handleMessageMultipleChanges(
                  { body: e.target.value },
                  nodes,
                  setNodes,
                );
                setModifications(true);
              }}
              maxLength={1024}
              autoComplete="off"
              disabled={nodeData.isTemplate}
              invalid={!nodeData.body}
            />
            {!nodeData.body && <ErrorMessage>Body is required</ErrorMessage>}
          </Field>

          <Field>
            <Legend>Footer</Legend>
            <div className="flex flex-row">
              <InputGroup className="flex relative items-center">
                <Input
                  name="Footer"
                  value={nodeData.footer ?? ""}
                  onChange={(e) =>
                    handleMessageMultipleChanges(
                      { footer: e.target.value },
                      nodes,
                      setNodes,
                    )
                  }
                  placeholder="Message footer"
                  autoComplete="off"
                  maxLength={60}
                  showCharacterCount
                />
              </InputGroup>
            </div>
            {errors.footerMissingButton && (
              <ErrorMessage>
                There must be at least one button to include footer
              </ErrorMessage>
            )}
          </Field>

          <Field>
            <Legend>Selector title</Legend>
            <div className="flex flex-row">
              <InputGroup className="flex relative items-center">
                <Input
                  name="Selector title"
                  value={nodeData.buttonTitle ?? ""}
                  onChange={(e) =>
                    handleMessageMultipleChanges(
                      { buttonTitle: e.target.value },
                      nodes,
                      setNodes,
                    )
                  }
                  placeholder="Pick an option"
                  autoComplete="off"
                  maxLength={20}
                  showCharacterCount
                  invalid={!nodeData.buttonTitle}
                />
              </InputGroup>
            </div>
            {!nodeData.buttonTitle && (
              <ErrorMessage>Selector title is required</ErrorMessage>
            )}
          </Field>

          {nodeData?.buttons?.map((button, index) => {
            return (
              <Field key={index}>
                <Legend key={`Legend ${index + 1}`}>{`Button ${
                  index + 1
                }`}</Legend>
                <div className="flex flex-row">
                  <InputGroup className="flex relative items-center">
                    <Input
                      name={`Button ${index + 1}`}
                      key={`Input ${index + 1}`}
                      value={button.title ?? ""}
                      onChange={(e) =>
                        handleButtonChange(index, e.target.value, nodes)
                      }
                      placeholder={
                        index % 3 === 0
                          ? "Yes!"
                          : index % 3 === 1
                            ? "No..."
                            : "Hmmm"
                      }
                      autoComplete="off"
                      maxLength={24}
                      showCharacterCount
                    />
                  </InputGroup>
                  {!nodeData.isTemplate && (
                    <Button
                      color="optionButton"
                      key={`Button ${index + 1}`}
                      onClick={async () =>
                        await onDeleteButtonFunction({
                          buttonIndex: index,
                          api,
                          setEdges,
                          setNodes,
                          edges,
                          nodes,
                          nodeId,
                        })
                      }
                    >
                      <TrashIcon />
                    </Button>
                  )}
                </div>
                <div className="flex flex-row mt-1">
                  <InputGroup className="flex relative items-center">
                    <Input
                      name={`Description ${index + 1}`}
                      value={button.description ?? ""}
                      onChange={(e) =>
                        handleButtonChange(
                          index,
                          e.target.value,
                          nodes,
                          "description",
                        )
                      }
                      placeholder={`Description ${index + 1}`}
                      autoComplete="off"
                      maxLength={72}
                      showCharacterCount
                    />
                  </InputGroup>
                </div>
              </Field>
            );
          })}
          <br />
          <div ref={buttonsContainerRef} className="mb-3">
            {(nodeData?.buttons?.length ?? 0) < 10 &&
              !edges.find((e) => e.source === nodeId && !e.sourceHandle) &&
              !nodeData.isTemplate && (
                <>
                  <Text className="text-sm">You can add up to 10 buttons</Text>
                  <Button
                    color="secondaryButton"
                    onClick={() => addReplyButton({ isInteractiveList: true })}
                  >
                    <PlusIcon />
                    Add a button
                  </Button>
                </>
              )}
          </div>
        </div>
      )}

      {nodeDataType === "media" && (
        <>
          <ButtonToggleGroup
            label="Header"
            options={headerOptions}
            selectedValue={headerType}
            onSelect={updateHeaderType}
          />

          {headerType === headerTypes.audio ? (
            <>
              <Field>
                <Legend>Audio URL</Legend>
                <Input
                  name="Audio URL"
                  value={nodeData.audio ?? ""}
                  onChange={(e) =>
                    handleMessageMultipleChanges(
                      { audio: e.target.value },
                      nodes,
                      setNodes,
                    )
                  }
                  placeholder="https://exemple.com/"
                  autoComplete="off"
                  maxLength={2000}
                />
              </Field>
              <br />
              <Dragger
                name="file"
                showUploadList={false}
                maxCount={1}
                accept="audio/aac,audio/amr,audio/mpeg,audio/mp4,audio/ogg"
                disabled={isUploading}
                multiple={false}
                height={250}
                beforeUpload={(file) => {
                  const isValidSize = file.size <= 1024 * 1024 * 16; // 16MB
                  if (!isValidSize) {
                    message.error("File must be smaller than 16 MB!");
                  }
                  return isValidSize;
                }}
                action={(file) =>
                  handleUploadFile({
                    file: file,
                    setIsUploading: setIsUploading,
                    user: user,
                    selectedNode: selectedNode,
                    nodes: nodes,
                    setNodes: setNodes,
                    setModifications: setModifications,
                    selectedHeader: "audio",
                  })
                }
                onDrop={(e) => {
                  console.log("Dropped files", e.dataTransfer.files);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag-and-drop a file to import it
                </p>
                <p className="ant-upload-hint">
                  Please upload a .mp3 or .ogg file smaller than 16MB
                </p>
              </Dragger>
            </>
          ) : (
            <>
              {headerType === headerTypes.video && (
                <>
                  <Field>
                    <Legend>Video URL</Legend>
                    <Input
                      name="Video URL"
                      value={nodeData.video ?? ""}
                      onChange={(e) =>
                        handleMessageMultipleChanges(
                          { video: e.target.value },
                          nodes,
                          setNodes,
                        )
                      }
                      placeholder="https://exemple.com/"
                      autoComplete="off"
                      maxLength={2000}
                    />
                  </Field>
                  <br />
                  <Dragger
                    name="file"
                    showUploadList={false}
                    maxCount={1}
                    accept="video/mp4"
                    disabled={isUploading}
                    multiple={false}
                    height={250}
                    beforeUpload={(file) => {
                      const isValid = handleBeforeUploadFile({
                        file: file,
                        selectedHeader: "video",
                      });
                      return isValid;
                    }}
                    action={(file) =>
                      handleUploadFile({
                        file: file,
                        setIsUploading: setIsUploading,
                        user: user,
                        selectedNode: selectedNode,
                        nodes: nodes,
                        setNodes: setNodes,
                        setModifications: setModifications,
                        selectedHeader: "video",
                      })
                    }
                    onDrop={(e) => {
                      console.log("Dropped files", e.dataTransfer.files);
                    }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag-and-drop a file to import it
                    </p>
                    <p className="ant-upload-hint">
                      Please upload a .mp4 file smaller than 16MB
                    </p>
                  </Dragger>
                </>
              )}
              {headerType === headerTypes.document && (
                <>
                  <Field>
                    <Legend>Document name</Legend>
                    <Input
                      name="Document name"
                      value={nodeData.documentName ?? ""}
                      onChange={(e) =>
                        handleMessageMultipleChanges(
                          { documentName: e.target.value },
                          nodes,
                          setNodes,
                        )
                      }
                      placeholder="Great file"
                      autoComplete="off"
                      maxLength={64}
                    />
                    {errors.documentInvalid && (
                      <ErrorMessage>Document name is required</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Legend>Document URL</Legend>
                    <Input
                      name="Document name"
                      value={nodeData.document ?? ""}
                      onChange={(e) =>
                        handleMessageMultipleChanges(
                          { document: e.target.value },
                          nodes,
                          setNodes,
                        )
                      }
                      placeholder="https://exemple.com/"
                      autoComplete="off"
                      maxLength={2000}
                    />
                  </Field>
                  <br />
                  <Dragger
                    name="file"
                    showUploadList={false}
                    maxCount={1}
                    accept="application/pdf"
                    disabled={isUploading}
                    multiple={false}
                    height={250}
                    beforeUpload={(file) => {
                      const isValidSize = file.size <= 1024 * 1024 * 100; // 100MB
                      if (!isValidSize) {
                        message.error("File must be smaller than 100 MB!");
                      }
                      return isValidSize;
                    }}
                    action={(file) =>
                      handleUploadFile({
                        file: file,
                        setIsUploading: setIsUploading,
                        user: user,
                        selectedNode: selectedNode,
                        nodes: nodes,
                        setNodes: setNodes,
                        setModifications: setModifications,
                        selectedHeader: "document",
                      })
                    }
                    onDrop={(e) => {
                      console.log("Dropped files", e.dataTransfer.files);
                    }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag-and-drop a file to import it
                    </p>
                    <p className="ant-upload-hint">
                      Please upload a .pdf file smaller than 100MB
                    </p>
                  </Dragger>
                </>
              )}
              {headerType === headerTypes.image && (
                <>
                  <Field>
                    <Legend>Image URL</Legend>
                    <Input
                      name="Image URL"
                      value={nodeData.image ?? ""}
                      onChange={(e) =>
                        handleMessageMultipleChanges(
                          { image: e.target.value },
                          nodes,
                          setNodes,
                        )
                      }
                      autoComplete="off"
                      maxLength={2000}
                    />
                  </Field>
                  <br />
                  <Dragger
                    name="file"
                    showUploadList={false}
                    maxCount={1}
                    accept="image/jpeg,image/png,application/pdf"
                    disabled={isUploading}
                    multiple={false}
                    height={250}
                    beforeUpload={(file) => {
                      const isValid = handleBeforeUploadFile({
                        file: file,
                        setFile: setFile,
                        selectedHeader: "image",
                        setIsPanelOpened: setIsPanelOpened,
                        setIsConvertCompressDialogOpen:
                          setIsConvertCompressDialogOpen,
                        selectedNode: selectedNode,
                        selectedNodeConvertCompress:
                          selectedNodeConvertCompress,
                        setSelectedNodeConvertCompress:
                          setSelectedNodeConvertCompress,
                      });
                      return isValid;
                    }}
                    action={(file) =>
                      handleUploadFile({
                        file: file,
                        setIsUploading: setIsUploading,
                        user: user,
                        selectedNode: selectedNode,
                        nodes: nodes,
                        setNodes: setNodes,
                        setModifications: setModifications,
                        selectedHeader: "image",
                      })
                    }
                    onDrop={(e) => {
                      console.log("Dropped files", e.dataTransfer.files);
                    }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag-and-drop a file to import it
                    </p>
                    <p className="ant-upload-hint">
                      Please upload a .jpeg or .png file. It must be less than
                      5MB.
                    </p>
                  </Dragger>
                  <br />
                </>
              )}

              <BodyFormSection
                body={nodeData.body ?? ""}
                setBody={handleBodyChange}
                bodyRef={bodyRef}
                paramOptions={paramOptions}
                nodes={nodes}
                setNodes={setNodes}
                selectedNode={selectedNode}
                errors={errors}
              />

              <br />
              {nodeData.isWaMeLink && (
                <>
                  <Field>
                    <Legend>WhatsApp redirection link</Legend>
                    <Text className="max-w-[calc(28rem-2.5rem)]">
                      {generateWhatsappLink(
                        user.customer.phoneNumber,
                        nodeData.text,
                      )}
                    </Text>
                  </Field>

                  <Button
                    className="mt-4"
                    color="secondaryButton"
                    onClick={handleShowQrCode}
                  >
                    Generate a QR Code
                  </Button>

                  {isQrCodeVisible && <QrCodePreview link={currentWaMeLink} />}
                </>
              )}

              {nodeData.urlButton?.text ||
              nodeData.urlButton?.url ||
              (nodeData.buttons || []).length ? (
                <Field>
                  <Divider className="mt-5 mb-3" />
                  <Legend>Footer</Legend>
                  <InputGroup>
                    <Input
                      name="footer"
                      value={nodeData.footer ?? ""}
                      onChange={(e) => handleFooterChange(e.target.value)}
                      maxLength={60}
                      showCharacterCount
                      autoComplete="off"
                    />
                  </InputGroup>
                  {errors.footerMissingButton && (
                    <ErrorMessage>
                      There must be at least one button to include footer
                    </ErrorMessage>
                  )}

                  <Divider className="mt-5" />
                </Field>
              ) : (
                <>There must be at least one button to include footer</>
              )}

              <ButtonToggleGroup
                label="Buttons"
                options={buttonsModeOptions}
                selectedValue={buttonsMode}
                onSelect={updateButtonsMode}
                className="mb-3"
              />

              {(buttonsMode === WhatsappTemplateButtonTypeEnum.URL ||
                nodeData.urlButton?.text ||
                nodeData.urlButton?.url) && (
                <>
                  <Field>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Legend>Enable click tracking</Legend>
                        <Tooltip
                          content="Track clicks and engagement metrics for this link"
                          size="4"
                          color="gray-400"
                          iconClassName="flex items-center justify-center mt-2"
                        />
                      </div>
                      <SwitchOnOff
                        checked={nodeData.urlButton?.trackingEnabled ?? true}
                        onChange={handleTrackingToggleChanged}
                        color="dark/zinc"
                      />
                    </div>
                  </Field>

                  <InputGroup>
                    <ArrowTopRightOnSquareIcon />
                    <Input
                      value={nodeData.urlButton?.text ?? ""}
                      placeholder="Link title"
                      onChange={handleURLButtonChanged}
                      maxLength={20}
                      showCharacterCount
                      autoComplete="off"
                    />
                  </InputGroup>

                  <Input
                    className="mt-3"
                    value={decodeURIComponent(nodeData.urlButton?.url ?? "")}
                    ref={urlButtonLinkRef}
                    placeholder="Link URL"
                    onChange={handleURLButtonLinkChanged}
                    autoComplete="off"
                  />
                  {errors.urlButtonInvalid && (
                    <ErrorMessage>The link is required</ErrorMessage>
                  )}
                  {customFieldCountInUrl > 1 && (
                    <ErrorMessage>
                      Only one custom field is allowed per URL
                    </ErrorMessage>
                  )}
                  {urlVariableNotAtEnd && (
                    <ErrorMessage>
                      {"{{url_variable}}"} must be at the end of the URL
                    </ErrorMessage>
                  )}
                  {invalidCustomFieldInUrl && (
                    <ErrorMessage>
                      Custom field "{{ invalidCustomFieldInUrl }}" does not
                      exist
                    </ErrorMessage>
                  )}

                  {customFieldOptions.length > 0 && (
                    <>
                      <Field className="mt-3">
                        <Legend>Insert custom field</Legend>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <SelectInput
                              placeholder="Select a custom field"
                              selected={selectedCustomField}
                              onChange={setSelectedCustomField}
                              options={customFieldOptions}
                              disabled={hasCustomFieldInUrl}
                            />
                          </div>
                          <Button
                            color="secondaryButton"
                            onClick={addCustomFieldToUrl}
                            disabled={
                              !selectedCustomField || hasCustomFieldInUrl
                            }
                          >
                            Add custom field
                          </Button>
                        </div>
                      </Field>
                    </>
                  )}

                  <Button
                    className="my-2"
                    color="secondaryButton"
                    onClick={() => {
                      addUrlVariable(urlButtonLinkRef);
                    }}
                    disabled={
                      nodeData.urlButton?.url?.includes("{{url_variable}}") ||
                      nodeData.urlButton?.url?.includes("{{1}}")
                    }
                  >
                    Add url variable
                  </Button>
                </>
              )}

              {(nodeData.buttons?.length ||
                buttonsMode === WhatsappTemplateButtonTypeEnum.QUICK_REPLY) && (
                <div ref={buttonsContainerRef} className="mb-3">
                  {nodeData.buttons?.map((button, index) => {
                    return (
                      <Field key={index}>
                        <Legend key={`Legend ${index + 1}`}>
                          {`Button ${index + 1}`}{" "}
                        </Legend>
                        <div className="flex flex-row">
                          <InputGroup className="flex relative items-center">
                            <Input
                              name={`Button ${index + 1}`}
                              key={`Input ${index + 1}`}
                              value={button.title ?? ""}
                              onChange={(e) =>
                                handleButtonChange(index, e.target.value, nodes)
                              }
                              placeholder={
                                index === 0
                                  ? "Yes!"
                                  : index === 1
                                    ? "No..."
                                    : "Hmmm"
                              }
                              autoComplete="off"
                              disabled={nodeData.isTemplate}
                              maxLength={20}
                              showCharacterCount
                            />
                          </InputGroup>
                          {!nodeData.isTemplate && (
                            <Button
                              color="optionButton"
                              className="ml-1"
                              key={`Button ${index + 1}`}
                              onClick={async () => {
                                await onDeleteButtonFunction({
                                  buttonIndex: index,
                                  api,
                                  setEdges,
                                  setNodes,
                                  edges,
                                  nodes,
                                  nodeId,
                                });
                                if (!nodeData.buttons?.length) {
                                  handleMessageMultipleChanges(
                                    { footer: null },
                                    nodes,
                                    setNodes,
                                  );
                                }
                              }}
                            >
                              <TrashIcon />
                            </Button>
                          )}
                        </div>
                      </Field>
                    );
                  })}
                  <br />
                  {(nodeData?.buttons?.length ?? 0) < 3 &&
                    !edges.find(
                      (e) => e.source === nodeId && !e.sourceHandle,
                    ) &&
                    !nodeData.isTemplate && (
                      <>
                        <Text className="text-sm mb-2">
                          You can add up to 3 buttons
                        </Text>
                        <Button
                          color="secondaryButton"
                          onClick={addReplyButton}
                        >
                          <PlusIcon />
                          Add a button
                        </Button>
                      </>
                    )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default ManageMessage;
